import React, { useState } from "react";
import { X, Check, ImagePlus } from "lucide-react";
import { TOPICS, EDUCATIONAL_LEVELS } from "../../utils/materialTaxonomy";
import ImageCropModal, { cropStyle } from "../common/ImageCropModal";

const CATEGORIES = ["Book", "Note", "Exercise", "Quiz", "Lecture", "Course", "Material"];
const VISIBILITY_OPTIONS = ["Public", "Private", "Unlisted"];

// Lets the owner of a material fix up its metadata after upload — the
// backend (`PATCH /api/materials/:id`) already enforced ownership and
// supported these fields, there just wasn't a UI entry point to reach it.
const EditMaterialModal = ({ material, token, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: material.title || "",
    description: material.description || "",
    category: material.category || "Note",
    topic: material.topic || "",
    educationalLevel: material.educationalLevel || "",
    university: material.university || "",
    visibility: material.visibility || "Public",
  });
  const [cover, setCover] = useState({ url: material.cover_url || "", crop: material.cover_crop || null });
  const [coverModalOpen, setCoverModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const coverChanged = cover.url !== (material.cover_url || "")
    || JSON.stringify(cover.crop || null) !== JSON.stringify(material.cover_crop || null);

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError("Title cannot be empty.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/materials/${material.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save changes.");

      // The cover goes through its own endpoint so a new link can't skip
      // the content check that the plain metadata update doesn't run.
      if (coverChanged) {
        const coverRes = await fetch(`/api/materials/${material.id}/cover`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ url: cover.url, crop: cover.crop }),
        });
        const coverData = await coverRes.json();
        if (!coverRes.ok) throw new Error(coverData.error || "Failed to save the cover image.");
      }

      onSaved?.(data.material);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const selectClass = "w-full px-3 py-2 rounded-lg bg-muted border-0 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring";
  const inputClass = `${selectClass} placeholder:text-muted-foreground`;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h3 className="text-sm font-bold text-foreground">Edit Material</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl hover:bg-muted flex items-center justify-center transition-colors border-none cursor-pointer bg-transparent">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {error && <p className="text-xs text-destructive font-medium">{error}</p>}

          <div>
            <label className="text-xs font-bold text-foreground mb-1 block">Cover image</label>
            {cover.url ? (
              <div className="flex gap-2">
                <div className="relative w-32 aspect-[16/9] rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <img src={cover.url} alt="Cover" style={{ ...cropStyle(cover.crop), position: 'absolute', inset: 0 }} />
                </div>
                <div className="flex flex-col gap-1.5 justify-center">
                  <button
                    onClick={() => setCoverModalOpen(true)}
                    className="px-3 py-1.5 rounded-lg bg-muted text-foreground text-xs font-bold hover:bg-muted/70 border-none cursor-pointer"
                  >
                    Adjust
                  </button>
                  <button
                    onClick={() => setCover({ url: "", crop: null })}
                    className="px-3 py-1.5 rounded-lg bg-transparent border border-border text-muted-foreground hover:text-destructive text-xs font-bold cursor-pointer"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setCoverModalOpen(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-dashed border-border text-muted-foreground text-xs font-bold bg-transparent cursor-pointer hover:text-foreground"
              >
                <ImagePlus className="w-4 h-4" /> Add a cover image link
              </button>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-foreground mb-1 block">Title</label>
            <input className={inputClass} value={form.title} onChange={set("title")} />
          </div>

          <div>
            <label className="text-xs font-bold text-foreground mb-1 block">Description</label>
            <textarea className={`${inputClass} resize-none`} rows={3} value={form.description} onChange={set("description")} />
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-bold text-foreground mb-1 block">Category</label>
              <select className={selectClass} value={form.category} onChange={set("category")}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-bold text-foreground mb-1 block">Visibility</label>
              <select className={selectClass} value={form.visibility} onChange={set("visibility")}>
                {VISIBILITY_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-bold text-foreground mb-1 block">Topic</label>
              <select className={selectClass} value={form.topic} onChange={set("topic")}>
                <option value="">—</option>
                {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-bold text-foreground mb-1 block">Level</label>
              <select className={selectClass} value={form.educationalLevel} onChange={set("educationalLevel")}>
                <option value="">—</option>
                {EDUCATIONAL_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-foreground mb-1 block">University</label>
            <input className={inputClass} value={form.university} onChange={set("university")} placeholder="e.g. Baku State University" />
          </div>
        </div>

        <div className="p-5 pt-0 flex-shrink-0">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 border-none cursor-pointer"
          >
            {saving ? "Saving..." : <><Check className="w-4 h-4" /> Save Changes</>}
          </button>
        </div>
      </div>

      <ImageCropModal
        isOpen={coverModalOpen}
        title="Cover image"
        initialUrl={cover.url}
        initialCrop={cover.crop}
        onCancel={() => setCoverModalOpen(false)}
        onSave={({ url, crop }) => {
          setCover({ url, crop: url ? crop : null });
          setCoverModalOpen(false);
        }}
      />
    </div>
  );
};

export default EditMaterialModal;
